import { useEffect } from 'react'

import {
  InformationCircleIcon,
  ShoppingCartRemove01Icon
} from '@hugeicons/react'
import clsx from 'clsx'
import _ from 'lodash'
import { useWatch, useFormContext } from 'react-hook-form'

import { useResourceContext } from '@/shared/ui/Resource'

import { useDigitalProductStore } from '../hook'
import { variationBorderClass } from '../lib/borderClasses'
import { isSoldOut } from '../lib/stock'

export const DigitalProductStoreBottomSheetVariations = () => {
  const { id, reward_items, max_quantity_per_user } = useResourceContext()
  const { setValue } = useFormContext()
  // Sem `defaultValue`, `useWatch` lê `defaultValues` já no primeiro render;
  // com ele, lê o literal e só reconcilia na primeira emissão de estado do
  // form. Só este leitor não pode esperar: o efeito de auto-seleção abaixo roda
  // na montagem, antes da reconciliação, e o `setValue` que ele dispara é
  // irreversível — leria `null` e saltaria para a variação irmã disponível. Os
  // outros leitores do campo reconciliam a tempo e por isso seguem como estavam.
  const selectedVariationId = useWatch({ name: 'reward_item_id' })
  const { hasRequestedMaxQuantityPerUser } = useDigitalProductStore(id)

  const selectedVariation = reward_items.find(
    item => item.id === selectedVariationId
  )

  const selectVariation = variationId => {
    setValue('reward_item_id', variationId, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true
    })
  }

  // Conta só as variações com estoque, porque o opt-in de esgotados passou a
  // trazê-las todas; e só age sem seleção prévia, para não sobrescrever a
  // variação que o Participante tocou — abrir o cartão esgotado saltaria para
  // a irmã disponível e o bloqueio do rodapé nunca dispararia.
  useEffect(() => {
    if (selectedVariationId) return

    const variationsInStock = reward_items.filter(item => !isSoldOut(item))

    if (variationsInStock.length === 1) selectVariation(variationsInStock[0].id)
  }, [reward_items, selectedVariationId])

  if (hasRequestedMaxQuantityPerUser) {
    return (
      <div className="flex items-start gap-3 rounded-lg bg-gray-200 p-4 text-gray-700">
        <InformationCircleIcon
          size={20}
          className="mt-0.5 shrink-0 text-gray-500"
        />
        <div className="flex flex-col gap-0.5">
          <h4 className="font-semibold text-gray-800">
            Você já reservou o máximo deste item por usuário
          </h4>
          <span className="text-sm text-gray-500">
            Volte para a lojinha e escolha outro item para reservar
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <p>
        <strong>Variação:</strong>{' '}
        {selectedVariation?.name || (
          <i className="text-gray-500">Escolha uma opção abaixo</i>
        )}
      </p>
      <div className="flex flex-wrap gap-3">
        {reward_items.map((item, index) => (
          <DigitalProductStoreBottomSheetVariation
            key={index}
            {...item}
            selectVariation={selectVariation}
          />
        ))}
      </div>
    </div>
  )
}

const DigitalProductStoreBottomSheetVariation = ({
  id,
  image,
  name,
  quantity,
  quantity_sold,
  selectVariation
}) => {
  // O mesmo predicado da auto-seleção acima. `getStock` daqui divergia dele com
  // `quantity_sold` ausente, e vinha de um `useDigitalProductStore()` que
  // assinava a consulta de recompensas uma vez por tile.
  const soldOut = isSoldOut({ quantity, quantity_sold })

  const selectedVariationId = useWatch({
    name: 'reward_item_id',
    defaultValue: null
  })

  /* `role="img"` torna a subárvore apresentacional, então o estado precisa
     estar no nome: sem isso o leitor de tela anunciava a palavra "Esgotado"
     solta, sem o item a que pertence, e o tile — `div` com `onClick`, sem foco
     nem teclado — não tinha nome nenhum. Dar-lhe `role="button"` seria prometer
     um controle que ele não honra; a operabilidade por teclado destes tiles é
     dívida anterior a este grupo. */
  return (
    <div
      role="img"
      aria-label={soldOut ? `${name} — esgotado` : name}
      className={clsx(
        'relative w-[100px] h-[100px] overflow-hidden rounded-lg bg-gray-100 shrink-0 border-[3px]',
        variationBorderClass(selectedVariationId === id),
        soldOut && 'pointer-events-none'
      )}
      onClick={() => (soldOut ? null : selectVariation(id))}
    >
      {/* A opacidade desce para a camada da imagem, e não para o tile inteiro:
          sobre 35% o rótulo perderia o contraste que a faixa existe para ter. */}
      <div
        className={clsx(
          'absolute inset-0 bg-no-repeat bg-center bg-cover',
          soldOut && 'opacity-35'
        )}
        style={{
          backgroundImage: `url('${_.get(
            image,
            'file.url',
            '/placeholders/reward.jpg'
          )}')`
        }}
      />
      {/* Faixa dentro do tile, e não linha ao lado dele: a 375px a fileira é de
          quadrados de 100px com `shrink-0`, e qualquer texto irmão empurraria a
          quebra (MASTER §7 L2). Âmbar e não o cinza de `locked`, como o rótulo
          de bloqueio do cartão da vitrine — `warning-text` sobre
          `warning-light` mede 4.63:1. O ícone não é enfeite: `text-2xs` é token
          de piso, e o MASTER §3 só o autoriza em rótulo redundante, com ícone e
          cor carregando a mesma informação da palavra. */}
      {soldOut && (
        <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-0.5 bg-warning-light px-1 py-0.5 text-2xs font-semibold leading-tight text-warning-text">
          <ShoppingCartRemove01Icon size={11} variant="solid" aria-hidden />
          Esgotado
        </span>
      )}
    </div>
  )
}
